import { useEffect, useMemo, useState } from 'react'
import { ArrowRightIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { env } from '@campai/lib-web/env'
import { Actor, useUserActor } from '@campai/lib-web/components/actor'
import { OrganizationAvatar } from '@campai/lib-web/components/OrganizationAvatar'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@campai/lib-web/components/new/ui'
import { RouterOutput, TRPCClientError, client } from '@campai/lib-web/utils/trpc'

import { appState } from '@/appState'
import { Page } from '@/components/Page'

type InvitationData = RouterOutput['organizations']['users']['invitations']['getOrganizationInvitation']

const InvitationContent = observer(
    ({ invitationData: { organization, access }, token }: { invitationData: InvitationData; token: string }) => {
        const { t } = useTranslation()
        const [isProcessingLogout, setIsProcessingLogout] = useState(false)
        const [isProcessingAccept, setIsProcessingAccept] = useState(false)

        const currentOrganizationUser = useMemo(
            () =>
                organization &&
                appState.user.organizations.find((org) => org.organization.toString() === organization._id.toString()),
            [organization]
        )

        const isOwner = !!currentOrganizationUser?.owner

        const handleLogout = async () => {
            setIsProcessingLogout(true)
            const url = new URL(window.location.href)
            await appState.auth.logout({
                postRedirectTo: url.pathname + url.search,
            })
        }

        const handleAccept = async () => {
            setIsProcessingAccept(true)
            try {
                await client.organizations.users.invitations.acceptOrganizationInvitation.mutate({
                    invitationToken: token,
                })
                window.location.replace(`${env.VITE_PRODUCT_URL_ADMIN}/${organization!.shortId}`)
            } catch (error) {
                if (error instanceof TRPCClientError) {
                    alert(error.message)
                }
                throw error
            }
        }

        const actor = useUserActor(appState.user)

        return (
            <Card className="mx-auto max-w-2xl">
                <CardContent className="space-y-6 p-6">
                    <div className="flex items-center justify-center gap-4">
                        <Actor actor={actor} size="default" className="size-14" />
                        <ArrowRightIcon className="text-muted-foreground size-4" />
                        <OrganizationAvatar
                            name={organization.name}
                            avatar={organization.avatar}
                            className="size-14 data-[square=true]:rounded-xl data-[square=true]:after:rounded-xl"
                        />
                    </div>

                    <div className="text-center text-lg font-medium">
                        {t('invitation.message', {
                            userName: appState.user.name,
                            organizationName: organization.name,
                        })}
                    </div>

                    {isOwner ? (
                        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-100">
                            <div className="font-medium">{t('invitation.areUserAlready')}</div>
                            <div className="mt-1">{t('invitation.isOwner')}</div>
                        </div>
                    ) : currentOrganizationUser && access.role ? (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-100">
                            <div className="font-medium">{t('invitation.areUserAlready')}</div>
                            <div className="mt-1">{t('invitation.roleChangeWarning')}</div>
                        </div>
                    ) : (
                        currentOrganizationUser && (
                            <div className="rounded-xl border border-black/10 bg-neutral-50 p-4 text-sm dark:border-white/10 dark:bg-neutral-900/60">
                                <div className="font-medium">{t('invitation.areUserAlready')}</div>
                                <div className="mt-1">{t('invitation.info')}</div>
                            </div>
                        )
                    )}

                    <div className="flex flex-col items-center gap-3">
                        {!isOwner && (
                            <Button
                                onClick={() => void handleAccept()}
                                disabled={isProcessingLogout}
                                loading={isProcessingAccept}
                            >
                                {t('invitation.accept')}
                            </Button>
                        )}
                        <Button
                            variant="outline"
                            onClick={() => void handleLogout()}
                            disabled={isProcessingAccept}
                            loading={isProcessingLogout}
                        >
                            {t('invitation.loginWithAnotherUser')}
                        </Button>
                    </div>
                </CardContent>
            </Card>
        )
    }
)

export const Invitation = observer(() => {
    const { t } = useTranslation()
    const token = useMemo(() => new URLSearchParams(window.location.search).get('token'), [])
    const [invitationData, setInvitationData] = useState<InvitationData | null | undefined>(undefined)

    useEffect(() => {
        void (async () => {
            if (!token) {
                setInvitationData(null)
                return
            }

            try {
                const result = await client.organizations.users.invitations.getOrganizationInvitation.query({
                    invitationToken: token,
                })
                setInvitationData(result)
            } catch {
                setInvitationData(null)
            }
        })()
    }, [token])

    if (invitationData === undefined) {
        return <Page title={t('invitation.title')} headerClassName="mx-auto w-full max-w-2xl" />
    }

    if (!invitationData) {
        return (
            <Page title={t('invitation.title')} headerClassName="mx-auto w-full max-w-2xl">
                <Card className="max-w-xl border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30">
                    <CardHeader>
                        <CardTitle>{t('invitation.error.title')}</CardTitle>
                        <CardDescription>{t('invitation.error.subtitle')}</CardDescription>
                    </CardHeader>
                </Card>
            </Page>
        )
    }

    return (
        <Page title={t('invitation.title')} headerClassName="mx-auto w-full max-w-2xl">
            <InvitationContent invitationData={invitationData} token={token!} />
        </Page>
    )
})
